import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BookOpen, FileText, Video, Link as LinkIcon, Download, Plus, Search,
  User, Award, ChevronRight, GraduationCap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CoursesPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      // Try getting from full profile (includes grades for students)
      if (user?.role === 'student') {
        const res = await axios.get(`${API_URL}/api/students/me/full-profile`);
        if (res.data.courses && res.data.courses.length > 0) {
          setCourses(res.data.courses);
          setLoading(false);
          return;
        }
      }
      // Fallback: try courses API
      const res = await axios.get(`${API_URL}/api/courses`);
      setCourses(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course =>
    course.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isTeacher = user?.role === 'teacher';
  const isAdmin = ['super_admin', 'rector', 'dean', 'department_head'].includes(user?.role);

  const l = language === 'fr' ? {
    coursesThisSemester: 'cours ce semestre',
    searchCourses: 'Rechercher un cours...',
    noCourses: 'Aucun cours trouve',
    trySearch: 'Essayez de modifier votre recherche',
    addCourse: 'Ajouter un cours',
    details: 'Details du cours',
    materials: 'Supports',
    assignments: 'Travaux',
    myGrade: 'Ma note',
    info: 'Infos',
    noMaterials: 'Aucun support disponible',
    noAssignments: 'Aucun TP pour le moment',
    downloadMaterial: 'Telecharger les supports',
  } : {
    coursesThisSemester: 'courses this semester',
    searchCourses: 'Search courses...',
    noCourses: 'No courses found',
    trySearch: 'Try adjusting your search',
    addCourse: 'Add Course',
    details: 'Course Details',
    materials: 'Materials',
    assignments: 'Assignments',
    myGrade: 'My Grade',
    info: 'Info',
    noMaterials: 'No materials available',
    noAssignments: 'No assignments yet',
    downloadMaterial: 'Download materials',
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="courses-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2">{t('courses')}</h1>
          <p className="text-gray-500 mt-1">{courses.length} {l.coursesThisSemester}</p>
        </div>
        {(isTeacher || isAdmin) && (
          <AddCourseDialog onAdd={fetchCourses} />
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder={l.searchCourses}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="course-search-input"
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6"><div className="h-4 w-20 bg-gray-200 rounded mb-3" /><div className="h-6 w-48 bg-gray-200 rounded mb-2" /><div className="h-4 w-32 bg-gray-200 rounded" /></CardContent>
            </Card>
          ))}
        </div>
      ) : filteredCourses.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{l.noCourses}</h3>
            <p className="text-gray-500">{l.trySearch}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCourses.map(course => (
            <CourseCard key={course.id} course={course} onClick={() => setSelectedCourse(course)} language={language} t={t} l={l} />
          ))}
        </div>
      )}

      {selectedCourse && (
        <CourseDetailModal course={selectedCourse} onClose={() => setSelectedCourse(null)} language={language} t={t} l={l} isTeacher={isTeacher} />
      )}
    </div>
  );
};

const CourseCard = ({ course, onClick, language, t, l }) => (
  <Card className="card-hover cursor-pointer group" onClick={onClick} data-testid={`course-card-${course.code}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between mb-3">
        <Badge variant="secondary" className="text-xs font-mono">{course.code}</Badge>
        <Badge className="bg-blue-100 text-blue-700">{course.credits} {language === 'fr' ? 'credits' : 'credits'}</Badge>
      </div>
      <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-[var(--tenant-primary)] transition-colors">{course.name}</h3>
      <div className="flex items-center justify-between text-sm mt-4">
        <div className="flex items-center gap-2 text-gray-500">
          <User className="w-4 h-4" />
          <span className="truncate max-w-[140px]">{course.teacher_name || 'N/A'}</span>
        </div>
        {course.grade && (
          <Badge className={course.grade.score / course.grade.max_score >= 0.5 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
            {course.grade.score}/{course.grade.max_score}
          </Badge>
        )}
      </div>
      {course.coefficient && (
        <div className="text-xs text-gray-400 mt-2">
          Coeff: {course.coefficient} | Sem: {course.semester}
        </div>
      )}
    </CardContent>
  </Card>
);

const CourseDetailModal = ({ course, onClose, language, t, l, isTeacher }) => {
  const [materials, setMaterials] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(true);

  useEffect(() => {
    loadCourseData();
  }, [course.id]);

  const loadCourseData = async () => {
    try {
      const [matRes, assignRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/courses/${course.id}/materials`),
        axios.get(`${API_URL}/api/courses/${course.id}/assignments`)
      ]);
      if (matRes.status === 'fulfilled') setMaterials(matRes.value.data || []);
      if (assignRes.status === 'fulfilled') setAssignments(assignRes.value.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMaterials(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="font-mono">{course.code}</Badge>
            <Badge className="bg-blue-100 text-blue-700">{course.credits} {language === 'fr' ? 'credits' : 'credits'}</Badge>
            {course.grade && (
              <Badge className={course.grade.score / course.grade.max_score >= 0.5 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                {l.myGrade}: {course.grade.score}/{course.grade.max_score}
              </Badge>
            )}
          </div>
          <DialogTitle className="text-xl">{course.name}</DialogTitle>
          <p className="text-sm text-gray-500">{course.teacher_name}</p>
        </DialogHeader>

        <Tabs defaultValue="info" className="mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="info">{l.info}</TabsTrigger>
            <TabsTrigger value="materials">{l.materials}</TabsTrigger>
            <TabsTrigger value="assignments">{l.assignments}</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-500">{t('semester')}</Label>
                <p className="mt-1 font-medium">{language === 'fr' ? 'Semestre' : 'Semester'} {course.semester}</p>
              </div>
              <div>
                <Label className="text-gray-500">{t('credits')}</Label>
                <p className="mt-1 font-medium">{course.credits} ECTS</p>
              </div>
              <div>
                <Label className="text-gray-500">Coefficient</Label>
                <p className="mt-1 font-medium">{course.coefficient || 1}</p>
              </div>
              <div>
                <Label className="text-gray-500">Max</Label>
                <p className="mt-1 font-medium">/{course.max_score || 20}</p>
              </div>
            </div>
            {course.grade && (
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Award className={`w-8 h-8 ${course.grade.score / course.grade.max_score >= 0.5 ? 'text-green-600' : 'text-red-500'}`} />
                    <div>
                      <p className="font-bold text-lg">{course.grade.score}/{course.grade.max_score}</p>
                      <p className="text-xs text-gray-500">{l.myGrade} ({course.grade.type})</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="materials" className="space-y-3 mt-4">
            {isTeacher && (
              <Button size="sm" className="gap-2 mb-4"><Plus className="w-4 h-4" />{t('addMaterial')}</Button>
            )}
            {loadingMaterials ? (
              <div className="text-center py-4 text-gray-400">{language === 'fr' ? 'Chargement...' : 'Loading...'}</div>
            ) : materials.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>{l.noMaterials}</p>
              </div>
            ) : materials.map(material => (
              <div key={material.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  material.type === 'pdf' ? 'bg-red-100 text-red-600' :
                  material.type === 'video' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'
                }`}>
                  {material.type === 'pdf' ? <FileText className="w-5 h-5" /> :
                   material.type === 'video' ? <Video className="w-5 h-5" /> :
                   <LinkIcon className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{material.title}</p>
                  <p className="text-xs text-gray-500 uppercase">{material.type}</p>
                </div>
                {material.file_url && (
                  <a href={`${API_URL}/api/files/${material.file_url}`} download>
                    <Button variant="ghost" size="icon" data-testid={`download-material-${material.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </a>
                )}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="assignments" className="mt-4">
            {assignments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">{l.noAssignments}</div>
            ) : assignments.map(a => (
              <div key={a.id} className="flex items-center justify-between p-3 border rounded-lg mb-2">
                <div>
                  <p className="font-medium">{a.title}</p>
                  <p className="text-xs text-gray-500">{a.type} - Max: {a.max_score}</p>
                </div>
                {a.due_date && (
                  <Badge variant="outline">{new Date(a.due_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</Badge>
                )}
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const AddCourseDialog = ({ onAdd }) => {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="add-course-button">
          <Plus className="w-4 h-4" />{language === 'fr' ? 'Ajouter un cours' : 'Add Course'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{language === 'fr' ? 'Nouveau cours' : 'New Course'}</DialogTitle>
        </DialogHeader>
        <form className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t('code')}</Label><Input placeholder="INFO101" /></div>
            <div><Label>{t('credits')}</Label><Input type="number" placeholder="6" /></div>
          </div>
          <div><Label>{t('name')}</Label><Input placeholder={language === 'fr' ? 'Nom du cours' : 'Course name'} /></div>
          <div><Label>{t('description')}</Label><Textarea placeholder={language === 'fr' ? 'Description du cours...' : 'Course description...'} /></div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t('cancel')}</Button>
            <Button type="submit">{t('save')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CoursesPage;
